// @ts-strict-ignore
// eslint-disable-next-line no-restricted-imports
import React from 'react';
import produce from 'immer';

import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {LocalizationStore} from 'common/i18n/store';

import {syncMetricsAcrossBookmark} from '../hydrate-bookmark';
import {ApplyParamsFunction, ReportBookmarkManagerBookmark, ReportState} from './types';

/**
 * Provides a Panel context that hosts a @type{Bookmark} instance and utilities to apply immutable modifiers
 *
 * This context also provides an instance of @type{ReportState} which is propagated as a React context value
 * inside @see{useReportBookmark}. This is done to avoid propagating Panel context internals to React components
 * in React hooks.
 */
export class ReportBookmarkManager {
  private declare _onApplyParams: ApplyParamsFunction;
  private _bookmark: ReportBookmarkManagerBookmark | null = null;

  private metrics: MetricsResource = null;
  private behaviors: BehaviorsResource = null;
  private i18n: LocalizationStore = null;

  applyParams(params, options) {
    this._onApplyParams(params, options);
  }

  updateBookmark(bookmark: ReportBookmarkManagerBookmark) {
    const hasInsightsBookmark = `sections` in (this._bookmark?.params ?? {});
    this._bookmark = produce(bookmark, (draft) => {
      /**
       * makes sure any edits to saved entities are reflected in the bookmark
       * this isn't necessary when loading a bookmark since the backend
       * will return a fully hydrated bookmark
       */
      if (`sections` in draft.params) {
        draft.params = syncMetricsAcrossBookmark({
          params: draft.params,
          previousParams: hasInsightsBookmark ? this.bookmark.params : null,
          metrics: this.metrics,
          behaviors: this.behaviors,
          i18n: this.i18n,
        });
      }
    });
  }

  public syncBookmark(bookmark: ReportBookmarkManagerBookmark) {
    this.updateBookmark(bookmark);
    return this.bookmark;
  }

  get bookmark() {
    return this._bookmark;
  }

  public setup(
    onApplyParams: ApplyParamsFunction,
    {
      metrics,
      behaviors,
      i18n,
    }: {
      metrics: MetricsResource;
      behaviors: BehaviorsResource;
      i18n: LocalizationStore;
    },
  ) {
    this._onApplyParams = onApplyParams;
    this.metrics = metrics;
    this.behaviors = behaviors;
    this.i18n = i18n;
  }

  public teardown() {
    this._onApplyParams = null;
    this._bookmark = null;
  }
}

export const ReportBookmarkManagerContext = React.createContext<ReportState>(
  // @ts-ignore
  {
    params: null,
  },
);

export const ReportBookmarkManagerConsumer = ReportBookmarkManagerContext.Consumer;

export const useReportBookmark = (): ReportState => {
  const contextValue = React.useContext(ReportBookmarkManagerContext);
  if (contextValue instanceof ReportBookmarkManager) {
    throw new Error(`Attempting to use a Panel context inside a React component`);
  }

  return contextValue;
};
